import { Entries } from '@eformulieren/model'
import type { LoginOption, LoginState } from '@eformulieren/renderer'

/**
 * Van een directe link naar het inloggen (R-154, R-203, R-64c, G017, ontwerp 3.3, opdracht 5c).
 *
 * Het processysteem toont de link naar een instantie, en een uitnodiging komt als link in een mail
 * (R-64b). Beide zijn gewone URL's en geen sleutel: wie ze opent zonder geldige sessie voor deze
 * publicatie, krijgt van `/whoami` een 401. Tot deze opdracht stond er dan een melding en kwam
 * niemand verder, want de pod kende de inlogdienst niet. Nu vraagt de pagina de pod langs welke
 * ingangen er in te loggen is (`GET /ingangen`), en biedt per ingang een knop naar
 * `<inlogdienst>/<gastheer>/starten?doel=<het adres van de pagina>`. De inlogdienst stuurt na het
 * inloggen terug naar dat adres, met het cookie voor deze publicatie; met een lopende sessie bij de
 * inlogdienst zonder gang langs de identiteitsprovider (G017).
 *
 * **Het doel is het adres zonder fragment.** Op `/uitnodiging` is het fragment de code (R-64c), en
 * die gaat niet naar een andere dienst: ze staat al in dit tabblad (`uitnodiging.ts`) en komt na
 * het inloggen daar weer vandaan. De inlogdienst weigert een doel met een fragment ook zelf. De
 * query blijft, want voorvullen loopt via de querystring (R-37).
 *
 * Dit bestand is `window`-vrij, op `getEntries` na, zodat de beslissingen in Node te toetsen zijn
 * (dezelfde keuze als `contextstand.ts`).
 */

/** Wat de pagina van haar eigen adres nodig heeft; `window.location` past erin. */
export interface PageLocation {
  readonly origin: string
  readonly pathname: string
  readonly search: string
  readonly hash: string
}

/** Het adres waar de inlogdienst na het inloggen naar terugstuurt: deze pagina, zonder fragment. */
export function targetFromPage(location: PageLocation): string {
  return `${location.origin}${location.pathname}${location.search}`
}

/**
 * Een knop per ingang, in de volgorde van de pod.
 *
 * Het doel gaat als één queryparameter mee en wordt dus gecodeerd: zijn eigen query hoort bij het
 * doel en niet bij het verzoek aan de inlogdienst. Met `anderAccount` voor "Niet u?":
 * `account=kiezen`, zodat een lopende sessie bij de inlogdienst of de provider niet meteen hetzelfde
 * account terugstuurt.
 */
export function loginOptions(entries: Entries, target: string, options: { readonly anderAccount?: boolean } = {}): readonly LoginOption[] {
  return entries.ingangen.map((entry) => {
    const address = new URL(`${entries.inlogdienst}/${encodeURIComponent(entry.gastheer)}/starten`)
    address.searchParams.set('doel', target)
    // "Niet u?": de inlogdienst slaat zijn sessie over en laat de provider het account kiezen (R-64c).
    if (options.anderAccount === true) address.searchParams.set('account', 'kiezen')
    return { gastheer: entry.gastheer, kant: entry.kant, adres: address.href }
  })
}

/**
 * Wat de pagina toont als er geen sessie is: de keuze, of een storing als de pod zijn ingangen niet
 * noemde. Nooit een pagina zonder uitweg en nooit een leeg formulier (valkuil 6).
 */
export function withoutSession(entries: Entries | undefined, target: string): LoginState {
  return entries === undefined ? { soort: 'storing' } : { soort: 'inloggen', opties: loginOptions(entries, target) }
}

/**
 * De ingangen van de eigen pod (`GET /ingangen`, `apps/runtime/src/ingangen.ts`), gelezen met het
 * schema uit het model, of niets bij tegenslag. Nooit een geworpen fout: de aanroeper toont dan de
 * storing.
 */
export async function getEntries(): Promise<Entries | undefined> {
  try {
    const response = await fetch('/ingangen')
    if (!response.ok) return undefined
    const read = Entries.safeParse(await response.json())
    return read.success ? read.data : undefined
  } catch {
    return undefined
  }
}

/** Hoe vaak en met welke tussenpozen de pagina `/ingangen` probeert (`getEntriesWithRetry`). */
export interface EntriesRetry {
  /** Het aantal pogingen, de eerste meegeteld. */
  readonly attempts: number
  /** De wachttijd vóór de tweede poging; daarna steeds het dubbele, tot `maxDelayMs`. */
  readonly firstDelayMs: number
  readonly maxDelayMs: number
}

/**
 * Op het formulier: zes pogingen in ruim twintig seconden. Lang genoeg voor een pod die net opkomt of
 * een haperende verbinding, en kort genoeg dat de pagina niet minutenlang blijft vragen; de waarschuwing
 * en het verlopen vragen daarna elk nog eens (`FormPage`).
 */
export const ENTRIES_RETRY_FORM: EntriesRetry = { attempts: 6, firstDelayMs: 1000, maxDelayMs: 8000 }

/**
 * Zonder sessie (`main.tsx`): drie pogingen binnen anderhalve seconde. Daar wacht de invuller op het
 * scherm met de keuze, en is er na die tijd niets, dan de storing.
 */
export const ENTRIES_RETRY_WITHOUT_SESSION: EntriesRetry = { attempts: 3, firstDelayMs: 500, maxDelayMs: 1000 }

/**
 * `/ingangen` begrensd opnieuw proberen, met een wachttijd die verdubbelt (WCAG 2.2.1, R-139, R-154).
 *
 * Eén mislukt verzoek bij het opkomen liet de pagina tot nu toe de hele sessie zonder ingangen: geen
 * uitloggen en, zonder de eigen ingang uit `/whoami`, geen "Sessie verlengen" vóór het verlopen. Nu een
 * paar pogingen, en dan stoppen: de pagina blijft niet eindeloos vragen.
 *
 * Met het lezen, het wachten en het afbreken van buiten, zodat het in Node te toetsen is zonder te wachten
 * (dezelfde opzet als `waitUntilSaved` in `session.ts`). Werpt nooit: na de laatste poging niets.
 */
export async function getEntriesWithRetry(
  retry: EntriesRetry,
  options: {
    readonly read?: () => Promise<Entries | undefined>
    readonly sleep?: (ms: number) => Promise<void>
    /** Of de aanroeper niet meer wacht (de pagina is weg); dan geen volgende poging. */
    readonly cancelled?: () => boolean
  } = {},
): Promise<Entries | undefined> {
  const read = options.read ?? getEntries
  const sleep = options.sleep ?? (async (ms: number) => await new Promise<void>((done) => setTimeout(done, ms)))
  let delay = retry.firstDelayMs
  for (let attempt = 1; ; attempt += 1) {
    const entries = await read()
    if (entries !== undefined) return entries
    if (attempt >= retry.attempts || options.cancelled?.() === true) return undefined
    await sleep(delay)
    if (options.cancelled?.() === true) return undefined
    delay = Math.min(delay * 2, retry.maxDelayMs)
  }
}
